/* aviaf — configs hub
   Full-fledged in-browser editor for runnable config files.
   Read-only by default · git-aware · "upload to aviaf" flow.
*/

const { useState: useCfg, useEffect: useCfgE, useRef: useCfgR, useMemo: useCfgM, useLayoutEffect: useCfgL } = React;

/* ─── synthetic file bodies (TOML) ────────────────────────────── */
window.CONFIG_FILES = {
  "cfg-mgc-alpha": `# MGC Alpha 1h — production
# author  k.s.    validated 2026-04-02
# basis   r-001, r-005, r-008

[meta]
label   = "MGC Alpha 1h"
file    = "mgc_1h_alpha_20260402.toml"
created = 2026-04-02
edited  = 2026-04-02

[risk]
daily_loss_cap    = 1500
max_concurrent    = 2
max_per_strategy  = 2
mll_floor         = 145500
kill_on_dd_pct    = 0.04
flatten_at_ct     = "15:55"

[[strategy]]
id       = "sd-mgc-alpha"
class    = "alpha"
enabled  = true

  [strategy.symbol]
  root  = "MGC"
  venue = "COMEX"
  month = "M26"

  [strategy.sizing]
  contracts       = 2
  sizing_mode     = "atr"
  atr_lookback    = 14
  risk_per_trade  = 750

  [strategy.params]
  momentum_lookback = 20
  alpha_threshold   = 0.015
  sl_pct            = 0.006
  tp_pct            = 0.018
  persistence_bars  = 2

  [strategy.hours]
  session    = "rth"
  open_ct    = "08:30"
  close_ct   = "15:00"
  no_friday  = false

[execution]
broker         = "topstepx"
order_type     = "limit"
slippage_ticks = 1
max_retries    = 3

[logging]
level = "info"
tail  = "/var/log/aviaf/inst-02.log"
`,

  "cfg-nq-orb": `# NQ ORB 5m — production
# author  k.s.    validated 2026-04-03
# basis   r-002, r-003

[meta]
label   = "NQ ORB 5m"
file    = "nq_5m_orb_20260403.toml"
created = 2026-04-03
edited  = 2026-04-03

[risk]
daily_loss_cap   = 2000
max_concurrent   = 1
mll_floor        = 145500
kill_on_dd_pct   = 0.05

[[strategy]]
id      = "sd-nq-orb"
class   = "orb"
enabled = true

  [strategy.symbol]
  root  = "NQ"
  venue = "CME"
  month = "M26"

  [strategy.sizing]
  contracts      = 1
  sizing_mode    = "fixed"
  risk_per_trade = 1000

  [strategy.params]
  opening_range_minutes = 30
  breakout_buffer_ticks = 2
  sl_atr_mult           = 1.0
  tp_atr_mult           = 3.0
  cancel_after_ct       = "09:30"

[execution]
broker         = "topstepx"
order_type     = "stop_limit"
slippage_ticks = 2
max_retries    = 3
`,

  "cfg-combo": `# Combined MGC + NQ — shared $2k DLC
# author  k.s.    validated 2026-04-02
# basis   r-001, r-002, r-005

[meta]
label   = "Combined MGC + NQ"
file    = "combined_mgc1h_nq5m_20260402.toml"
created = 2026-04-02
edited  = 2026-04-02

[risk]
daily_loss_cap   = 2000
max_concurrent   = 2
mll_floor        = 145500
kill_on_dd_pct   = 0.04
shared_dlc       = true

[[strategy]]
id      = "sd-mgc-alpha"
class   = "alpha"
enabled = true
weight  = 0.50

  [strategy.symbol]
  root = "MGC"; venue = "COMEX"; month = "M26"

  [strategy.sizing]
  contracts = 1; sizing_mode = "atr"; risk_per_trade = 500

  [strategy.params]
  momentum_lookback = 20
  alpha_threshold   = 0.015
  sl_pct            = 0.006
  tp_pct            = 0.018

[[strategy]]
id      = "sd-nq-orb"
class   = "orb"
enabled = true
weight  = 0.50

  [strategy.symbol]
  root = "NQ"; venue = "CME"; month = "M26"

  [strategy.sizing]
  contracts = 1; sizing_mode = "fixed"; risk_per_trade = 500

  [strategy.params]
  opening_range_minutes = 30
  breakout_buffer_ticks = 2
  sl_atr_mult           = 1.0
  tp_atr_mult           = 3.0

[execution]
broker = "topstepx"
order_type = "limit"
`,

  "cfg-grind": `# MGC 1c grind (XFA survival)
# author  k.s.    validated 2026-04-02
# basis   r-001

[meta]
label   = "MGC 1c grind (XFA)"
file    = "m2k_1h_alpha_20260402.toml"
created = 2026-04-02
edited  = 2026-04-02

[risk]
daily_loss_cap   = 600
max_concurrent   = 1
mll_floor        = 47500
kill_on_dd_pct   = 0.025

[[strategy]]
id      = "sd-mgc-grind"
class   = "mr"
enabled = true

  [strategy.symbol]
  root = "MGC"; venue = "COMEX"; month = "M26"

  [strategy.sizing]
  contracts      = 1
  sizing_mode    = "fixed"
  risk_per_trade = 350

  [strategy.params]
  momentum_lookback = 20
  alpha_threshold   = 0.015
  sl_pct            = 0.006

[execution]
broker = "topstepx"
order_type = "limit"
`,
};

/* ─── demo config metadata ────────────────────────────────────
   Always-visible reference configs (one per CONFIG_FILES entry) so
   the Configs hub has something to render even when window.CONFIGS
   from the live agent is empty or uses different IDs.  Merged with
   live configs in the file tree — demos go under a separate folder,
   live ones under "live".
   _demo:true is the visibility flag; the editor uses it to skip the
   binding pill (which wires up real instances/accounts/strategies).
*/
window.CONFIGS_DEMO = [
  { id:"cfg-mgc-alpha", file:"mgc_1h_alpha_20260402.toml", label:"MGC Alpha 1h",
    note:"demo · single-strategy · MGC.M26 · 2c · ATR sized",
    strategyDefs:["sd-mgc-alpha"], dailyLossCap:1500, maxConcurrent:2,
    lastEdit:"2026-04-02", usedBy:[], _demo:true },
  { id:"cfg-nq-orb", file:"nq_5m_orb_20260403.toml", label:"NQ ORB 5m",
    note:"demo · single-strategy · NQ.M26 · 1c",
    strategyDefs:["sd-nq-orb"], dailyLossCap:2000, maxConcurrent:1,
    lastEdit:"2026-04-03", usedBy:[], _demo:true },
  { id:"cfg-combo", file:"combined_mgc1h_nq5m_20260402.toml", label:"Combined MGC + NQ",
    note:"demo · shared $2k DLC · MGC 1c + NQ 1c",
    strategyDefs:["sd-mgc-alpha","sd-nq-orb"], dailyLossCap:2000, maxConcurrent:2,
    lastEdit:"2026-04-02", usedBy:[], _demo:true },
  { id:"cfg-grind", file:"m2k_1h_alpha_20260402.toml", label:"MGC 1c grind (XFA)",
    note:"demo · survival mode · 1c · risk $350",
    strategyDefs:["sd-mgc-grind"], dailyLossCap:600, maxConcurrent:1,
    lastEdit:"2026-04-02", usedBy:[], _demo:true },
];

/* ─── persisted overrides per config (shape mirrors aviaf's persisted_overrides table)
   one row per active hub-side param tuning applied on top of TOML ─── */
window.CONFIG_OVERRIDES = {
  "cfg-mgc-alpha": [
    { path: "strategy.params.alpha_threshold", toml: "0.015", live: "0.017", by: "k.s.", when: "2h ago", line: 33, account: "TSX-100" },
  ],
  "cfg-combo": [
    { path: "risk.daily_loss_cap",            toml: "2000",  live: "1800",  by: "k.s.", when: "5h ago", line: 11, account: "" },
    { path: "strategy.sizing.contracts",      toml: "1",     live: "2",     by: "k.s.", when: "1d ago", line: 30, account: "TSX-100" },
  ],
  "cfg-nq-orb": [],
  "cfg-grind":  [],
};

/* ─── git history per file ───────────────────────────────────── */
window.CONFIG_GIT = {
  "cfg-mgc-alpha": [
    { sha:"a8f3d2c", msg:"tighten alpha_threshold 0.018 → 0.015", author:"k.s.", date:"4d ago", branch:"main" },
    { sha:"f2c1b89", msg:"add flatten_at_ct = 15:55",             author:"k.s.", date:"6d ago", branch:"main" },
    { sha:"7e1aa44", msg:"bump sl_pct to 0.006",                  author:"k.s.", date:"9d ago", branch:"main" },
    { sha:"b09c512", msg:"initial — extracted from r-005",        author:"k.s.", date:"2w ago", branch:"main" },
  ],
  "cfg-nq-orb": [
    { sha:"c41ee8a", msg:"cancel_after_ct: 09:32 → 09:30",        author:"k.s.", date:"3d ago", branch:"main" },
    { sha:"9d22b30", msg:"breakout_buffer_ticks 1 → 2",           author:"k.s.", date:"1w ago", branch:"main" },
    { sha:"0aa1f77", msg:"initial — extracted from r-002",        author:"k.s.", date:"3w ago", branch:"main" },
  ],
  "cfg-combo": [
    { sha:"e4f00d2", msg:"shared_dlc = true",                     author:"k.s.", date:"5d ago", branch:"main" },
    { sha:"71b88c1", msg:"weight 0.6/0.4 → 0.5/0.5",              author:"k.s.", date:"1w ago", branch:"main" },
    { sha:"4a90c80", msg:"initial combine",                       author:"k.s.", date:"2w ago", branch:"main" },
  ],
  "cfg-grind": [
    { sha:"3ee2110", msg:"risk_per_trade 400 → 350",              author:"k.s.", date:"1w ago", branch:"main" },
    { sha:"d18a4f0", msg:"initial — xfa survival profile",        author:"k.s.", date:"3w ago", branch:"main" },
  ],
};

/* ─── inject CSS ──────────────────────────────────────────────── */
(function injectCSS(){
  if (document.getElementById("cfg-editor-css")) return;
  const css = `
  .ce {
    display: grid;
    grid-template-columns: var(--ce-side-w, 260px) 1fr;
    height: calc(100vh - 48px - 30px);
    min-height: 540px;
    font-family: var(--mono);
    border-top: 1px solid var(--rule);
    background: var(--paper);
    position: relative;
  }
  .ce-side {
    position: relative;
    border-right: 1px solid var(--rule);
    background: var(--paper-2);
    display: flex; flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }
  .ce-resizer {
    position: absolute;
    top: 0; bottom: 0;
    left: var(--ce-side-w, 260px);
    width: 6px;
    margin-left: -3px;
    cursor: col-resize;
    z-index: 10;
    background: transparent;
  }
  .ce-resizer::after {
    content: "";
    position: absolute;
    top: 0; bottom: 0; left: 3px;
    width: 1px;
    background: transparent;
    transition: background 120ms;
  }
  .ce-resizer:hover::after,
  .ce-resizer[data-dragging="true"]::after {
    background: var(--accent);
  }
  body.ce-dragging { cursor: col-resize; user-select: none; }
  body.ce-dragging * { cursor: col-resize !important; }
  .ce-side-h {
    padding: 10px 14px;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
    border-bottom: 1px solid var(--rule);
    display: flex; justify-content: space-between; align-items: center;
  }
  .ce-side-h .ct { color: var(--ink); }
  .ce-tree-wrap {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    border-bottom: 1px solid var(--rule);
  }
  .ce-tree {
    padding: 4px 0 8px;
  }
  .ce-tree-row {
    display: grid;
    grid-template-columns: 14px 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    font-size: 12px;
    color: var(--ink-2);
    cursor: pointer;
    border-left: 2px solid transparent;
  }
  .ce-tree-row:hover { background: var(--paper-3); color: var(--ink); }
  .ce-tree-row[data-active="true"] {
    background: var(--paper-3);
    color: var(--ink);
    border-left-color: var(--accent);
  }
  .ce-tree-row .ic {
    width: 10px; height: 10px;
    border: 1px solid var(--rule-2);
    display: inline-block;
  }
  .ce-tree-row[data-active="true"] .ic { border-color: var(--accent); background: var(--accent-soft); }
  .ce-tree-row[data-dirty="true"] .ic { background: var(--warn); border-color: var(--warn); }
  .ce-tree-row .meta {
    font-size: 10px;
    color: var(--muted-2);
    letter-spacing: 0;
  }

  .ce-folder {
    padding: 10px 14px 4px;
    font-size: 9.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted-2);
  }
  .ce-folder::before { content: "▾ "; color: var(--muted-2); }

  .ce-bottom {
    flex: 0 0 auto;
    display: flex; flex-direction: column;
    background: var(--paper-2);
    min-height: 0;
    height: var(--ce-bottom-h, auto);
    border-top: 1px solid var(--rule);
  }
  .ce-bottom[data-collapsed="true"] { display: none; }
  .ce-bottom-resizer {
    flex: 0 0 auto;
    height: 6px;
    margin: -3px 0;
    cursor: row-resize;
    background: transparent;
    position: relative;
    z-index: 5;
  }
  .ce-bottom-resizer::after {
    content: "";
    position: absolute;
    left: 0; right: 0; top: 3px;
    height: 1px;
    background: transparent;
    transition: background 120ms;
  }
  .ce-bottom-resizer:hover::after,
  .ce-bottom-resizer[data-dragging="true"]::after { background: var(--accent); }
  body.ce-y-dragging { cursor: row-resize; user-select: none; }
  body.ce-y-dragging * { cursor: row-resize !important; }
  .ce-side-h .togg {
    background: transparent;
    border: 1px solid var(--rule);
    padding: 1px 6px;
    color: var(--muted);
    font-family: inherit;
    font-size: 10px;
    cursor: pointer;
    line-height: 1;
    margin-left: 6px;
    letter-spacing: 0.04em;
  }
  .ce-side-h .togg:hover { color: var(--ink); border-color: var(--ink-2); }
  .ce-empty-body {
    padding: 40px 20px;
    text-align: center;
    color: var(--muted);
    font-size: 12px;
    border-bottom: 1px solid var(--rule);
    background: var(--paper-2);
  }
  .ce-empty-body .h {
    color: var(--ink-2);
    font-size: 13px;
    margin-bottom: 6px;
  }
  .ce-branch {
    padding: 12px 14px;
    border-bottom: 1px solid var(--rule);
    display: flex; flex-direction: column; gap: 8px;
    flex: 0 0 auto;
  }
  .ce-branch-row {
    display: flex; align-items: center; justify-content: space-between;
    font-size: 11px;
  }
  .ce-branch-name {
    display: inline-flex; align-items: center; gap: 6px;
    color: var(--ink);
  }
  .ce-branch-name .ic {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--pos);
    box-shadow: 0 0 0 2px var(--paper-2), 0 0 0 3px var(--pos);
  }
  .ce-branch-pick {
    background: transparent; border: 0; padding: 0;
    color: var(--muted); font-family: inherit; font-size: 11px;
    cursor: pointer;
  }
  .ce-branch-pick:hover { color: var(--ink); }
  .ce-branch-status {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    font-size: 10px;
    color: var(--muted);
  }
  .ce-branch-status .s {
    padding: 4px 6px;
    border: 1px solid var(--rule);
    background: var(--paper);
  }
  .ce-branch-status .s .v {
    font-size: 13px;
    color: var(--ink);
    margin-top: 1px;
    letter-spacing: -0.01em;
  }
  .ce-branch-status .s.dirty .v { color: var(--warn); }

  .ce-hist-h {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px;
    font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--muted);
    border-bottom: 1px solid var(--rule);
    cursor: pointer;
    user-select: none;
  }
  .ce-hist-h:hover { color: var(--ink); }
  .ce-hist-h .ct { color: var(--ink); display: flex; align-items: center; gap: 6px; }
  .ce-hist-h .chev {
    display: inline-block; width: 8px; text-align: center;
    transition: transform 140ms;
    color: var(--muted-2);
  }
  .ce-hist-h[data-open="true"] .chev { transform: rotate(0deg); }
  .ce-hist-h[data-open="false"] .chev { transform: rotate(-90deg); }

  .ce-hist {
    overflow-y: auto;
    min-height: 0;
    flex: 1 1 auto;
    border-bottom: 1px solid var(--rule);
  }
  .ce-hist[data-open="false"] { display: none; }
  .ce-hist-row {
    padding: 8px 14px;
    border-bottom: 1px dashed var(--rule);
    display: grid;
    grid-template-columns: 56px 1fr;
    gap: 8px;
    align-items: start;
    font-size: 11px;
    cursor: pointer;
  }
  .ce-hist-row:hover { background: var(--paper-3); }
  .ce-hist-row .sha { color: var(--accent); }
  .ce-hist-row .msg { color: var(--ink); line-height: 1.4; }
  .ce-hist-row .sub { font-size: 10px; color: var(--muted); margin-top: 2px; }

  /* ── main pane ───────────────────────────────────────────── */
  .ce-main {
    display: grid;
    grid-template-rows: auto 1fr auto;
    min-width: 0;
    min-height: 0;
    background: var(--paper);
  }
  .ce-head {
    display: flex; align-items: center;
    padding: 0 14px;
    border-bottom: 1px solid var(--rule);
    background: var(--paper);
    gap: 14px;
    height: 42px;
    min-height: 42px;
    overflow: hidden;
  }
  .ce-crumbs {
    font-size: 12px;
    color: var(--muted);
    display: flex; align-items: center; gap: 6px;
    min-width: 0;
    flex-shrink: 0;
  }
  .ce-crumbs .sep { color: var(--muted-2); }
  .ce-crumbs .leaf { color: var(--ink); }
  .ce-crumbs .dirty {
    display: inline-block;
    width: 6px; height: 6px;
    background: var(--warn);
    margin-left: 4px;
  }
  .ce-crumbs .ro {
    margin-left: 6px;
    font-size: 9.5px;
    padding: 2px 6px;
    border: 1px solid var(--rule-2);
    color: var(--muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .ce-crumbs .ro[data-state="edit"] { color: var(--accent); border-color: var(--accent); }

  .ce-head-sep {
    width: 1px; height: 18px; background: var(--rule);
    flex-shrink: 0;
  }
  .ce-head-meta {
    display: flex; align-items: center; gap: 14px;
    font-size: 11px;
    color: var(--muted);
    min-width: 0;
    overflow: hidden;
    flex: 1;
  }
  .ce-head-meta .kv {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ce-head-meta .kv b {
    color: var(--ink); font-weight: 500; margin-left: 5px;
  }
  .ce-head-meta .pill {
    padding: 1px 6px; border: 1px solid var(--rule-2); color: var(--muted);
    font-size: 10px; letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .ce-head-meta .pill.live { color: var(--pos); border-color: var(--pos); }

  /* ── binding pill ──────────────────────────────────────── */
  .ce-actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
  .ce-bind {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 4px 9px;
    border: 1px solid var(--rule-2);
    background: var(--paper-2);
    color: var(--ink-2);
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.01em;
    cursor: pointer;
    flex-shrink: 0;
    white-space: nowrap;
    position: relative;
    transition: border-color 120ms, color 120ms;
  }
  .ce-bind:hover { color: var(--ink); border-color: var(--ink-2); }
  .ce-bind[data-open="true"] { color: var(--ink); border-color: var(--accent); }
  .ce-bind .led {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--pos);
    box-shadow: 0 0 0 2px var(--paper-2), 0 0 0 3px var(--pos);
    flex-shrink: 0;
  }
  .ce-bind .led.idle { background: var(--muted-2); box-shadow: 0 0 0 2px var(--paper-2), 0 0 0 3px var(--muted-2); }
  .ce-bind .led.warn { background: var(--warn); box-shadow: 0 0 0 2px var(--paper-2), 0 0 0 3px var(--warn); }
  .ce-bind .chip {
    color: var(--muted-2);
  }
  .ce-bind .chip b { color: var(--ink); font-weight: 500; }
  .ce-bind .sep { color: var(--muted-2); margin: 0 1px; }
  .ce-bind .ov  { color: var(--warn); }
  .ce-bind .chev { color: var(--muted-2); font-size: 9px; margin-left: 2px; }

  .ce-bind-pop {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 380px;
    background: var(--paper);
    border: 1px solid var(--rule);
    box-shadow: 0 8px 32px oklch(0 0 0 / 0.18);
    z-index: 150;
    font-family: var(--mono);
    cursor: default;
  }
  .ce-bind-pop .group {
    border-bottom: 1px solid var(--rule);
  }
  .ce-bind-pop .group:last-child { border-bottom: 0; }
  .ce-bind-pop .g-h {
    padding: 8px 14px 4px;
    font-size: 9.5px; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--muted-2);
    display: flex; justify-content: space-between; align-items: center;
  }
  .ce-bind-pop .g-h .ct { color: var(--ink-2); letter-spacing: 0; text-transform: none; font-size: 10px; }
  .ce-bind-pop .row {
    padding: 6px 14px 9px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px 10px;
    font-size: 11.5px;
    align-items: baseline;
  }
  .ce-bind-pop .row .nm { color: var(--ink); }
  .ce-bind-pop .row .meta {
    grid-column: 1 / -1;
    font-size: 10.5px;
    color: var(--muted);
    line-height: 1.5;
  }
  .ce-bind-pop .row .meta b { color: var(--ink-2); font-weight: 500; margin-right: 8px; }
  .ce-bind-pop .row .state {
    font-size: 10px;
    padding: 1px 5px;
    border: 1px solid var(--rule-2);
    color: var(--muted);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .ce-bind-pop .row .state.live { color: var(--pos); border-color: var(--pos); }
  .ce-bind-pop .row .state.idle { color: var(--muted-2); }
  .ce-bind-pop .row .src {
    font-size: 9.5px; color: var(--muted-2); letter-spacing: 0.06em;
    text-transform: uppercase; margin-left: 4px;
  }
  .ce-bind-pop a.link {
    color: var(--accent); text-decoration: none; cursor: pointer;
    font-size: 10.5px;
  }
  .ce-bind-pop a.link:hover { text-decoration: underline; }

  .ce-bind-pop .ovr {
    padding: 5px 14px 7px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 2px;
    border-top: 1px dashed var(--rule);
    font-size: 11px;
    cursor: pointer;
  }
  .ce-bind-pop .ovr:first-of-type { border-top: 0; }
  .ce-bind-pop .ovr:hover { background: var(--paper-3); }
  .ce-bind-pop .ovr .p {
    color: var(--ink);
    display: flex; align-items: center; gap: 6px;
    overflow: hidden;
  }
  .ce-bind-pop .ovr .p .dot {
    width: 5px; height: 5px; background: var(--warn);
    flex-shrink: 0;
  }
  .ce-bind-pop .ovr .p .pp {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    color: var(--muted);
  }
  .ce-bind-pop .ovr .p .pp .k { color: var(--ink); }
  .ce-bind-pop .ovr .v {
    font-size: 10.5px; color: var(--muted);
    padding-left: 11px;
  }
  .ce-bind-pop .ovr .v .from {
    text-decoration: line-through;
    color: var(--muted-2);
    margin-right: 6px;
  }
  .ce-bind-pop .ovr .v .to { color: var(--warn); }
  .ce-bind-pop .ovr .v .by { margin-left: 6px; color: var(--muted-2); }
  .ce-bind-pop .empty {
    padding: 6px 14px 10px;
    font-size: 10.5px; color: var(--muted);
  }

  /* override gutter dot */
  .ce-gutter .ln .ov-dot {
    position: absolute;
    left: 4px; top: 8px;
    width: 4px; height: 4px;
    background: var(--warn);
    pointer-events: none;
  }
  .ce-btn {
    background: transparent;
    color: var(--ink-2);
    border: 1px solid var(--rule-2);
    padding: 5px 10px;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.02em;
    cursor: pointer;
  }
  .ce-btn:hover { color: var(--ink); border-color: var(--ink-2); }
  .ce-btn.primary {
    background: var(--ink);
    color: var(--paper);
    border-color: var(--ink);
  }
  .ce-btn.primary:hover { background: var(--accent); border-color: var(--accent); color: var(--paper); }
  .ce-btn.danger {
    color: var(--neg); border-color: var(--neg);
  }
  .ce-btn.danger:hover { background: var(--neg); color: var(--paper); }
  .ce-btn[disabled] { opacity: 0.4; cursor: not-allowed; }

  .ce-meta {
    display: flex; align-items: center; gap: 18px;
    padding: 6px 16px;
    border-bottom: 1px solid var(--rule);
    background: var(--paper-2);
    font-size: 11px;
    color: var(--muted);
  }
  .ce-meta .kv b { color: var(--ink); font-weight: 500; margin-left: 4px; }
  .ce-meta .pill {
    padding: 1px 6px; border: 1px solid var(--rule-2); color: var(--muted);
  }
  .ce-meta .pill.live { color: var(--pos); border-color: var(--pos); }
  .ce-meta .spacer { flex: 1; }

  /* ── code area ───────────────────────────────────────────── */
  .ce-code {
    position: relative;
    overflow: hidden;
    background: var(--paper);
    min-height: 0;
  }
  .ce-scroll {
    position: absolute; inset: 0;
    overflow: auto;
    font-family: var(--mono);
    font-size: 13px;
    line-height: 20px;
  }
  .ce-grid {
    display: grid;
    grid-template-columns: 56px 1fr;
    min-height: 100%;
  }
  .ce-gutter {
    background: var(--paper-2);
    color: var(--muted-2);
    border-right: 1px solid var(--rule);
    text-align: right;
    padding: 12px 8px 12px 0;
    user-select: none;
    font-size: 11px;
    line-height: 20px;
  }
  .ce-gutter .ln {
    height: 20px;
    padding-right: 4px;
    position: relative;
  }
  .ce-gutter .ln.mod { color: var(--warn); }
  .ce-gutter .ln.add { color: var(--pos); }
  .ce-gutter .ln.mod::before,
  .ce-gutter .ln.add::before {
    content: "";
    position: absolute;
    right: 0; top: 0; bottom: 0;
    width: 2px;
    background: var(--warn);
  }
  .ce-gutter .ln.add::before { background: var(--pos); }

  .ce-pane {
    position: relative;
    min-width: 0;
  }
  .ce-pre, .ce-ta {
    margin: 0;
    padding: 12px 16px;
    font-family: var(--mono);
    font-size: 13px;
    line-height: 20px;
    white-space: pre;
    tab-size: 2;
    border: 0;
    background: transparent;
  }
  .ce-pre {
    color: var(--ink);
    pointer-events: none;
    user-select: text;
    overflow: visible;
  }
  .ce-ta {
    position: absolute; inset: 0;
    width: 100%;
    color: transparent;
    caret-color: var(--accent);
    resize: none;
    outline: none;
    overflow: visible;
  }
  .ce-ta::selection { background: var(--accent-soft); color: transparent; }

  /* highlight — token colors are themable via CSS vars */
  .ce-code {
    --tk-c:    var(--muted);
    --tk-sec:  var(--accent);
    --tk-key:  var(--ink);
    --tk-eq:   var(--muted);
    --tk-str:  var(--pos);
    --tk-num:  var(--warn);
    --tk-bool: var(--accent);
    --tk-date: var(--warn);
    --tk-arr:  var(--ink-2);
    --tk-punc: var(--muted-2);
    --tk-bg:   var(--paper);
    --tk-gutter-bg: var(--paper-2);
    background: var(--tk-bg);
  }
  .ce-gutter { background: var(--tk-gutter-bg) !important; }

  .ce-code[data-theme="mono"] {
    --tk-c:    var(--muted);
    --tk-sec:  var(--ink);
    --tk-key:  var(--ink);
    --tk-eq:   var(--muted-2);
    --tk-str:  var(--ink-2);
    --tk-num:  var(--ink);
    --tk-bool: var(--ink);
    --tk-date: var(--ink);
    --tk-arr:  var(--muted);
    --tk-punc: var(--muted-2);
  }
  .ce-code[data-theme="mono"] .tk-c { font-style: italic; }
  .ce-code[data-theme="mono"] .tk-sec,
  .ce-code[data-theme="mono"] .tk-key,
  .ce-code[data-theme="mono"] .tk-num,
  .ce-code[data-theme="mono"] .tk-bool { font-weight: 600; }

  .ce-code[data-theme="sunset"] {
    --tk-c:    oklch(0.62 0.04 60);
    --tk-sec:  oklch(0.55 0.18 25);
    --tk-key:  oklch(0.32 0.05 60);
    --tk-eq:   oklch(0.60 0.04 60);
    --tk-str:  oklch(0.55 0.15 145);
    --tk-num:  oklch(0.62 0.18 75);
    --tk-bool: oklch(0.55 0.18 25);
    --tk-date: oklch(0.55 0.18 290);
    --tk-arr:  oklch(0.45 0.06 60);
    --tk-punc: oklch(0.68 0.04 60);
  }

  .ce-code[data-theme="ocean"] {
    --tk-c:    oklch(0.60 0.04 240);
    --tk-sec:  oklch(0.55 0.16 240);
    --tk-key:  oklch(0.30 0.04 240);
    --tk-eq:   oklch(0.58 0.04 240);
    --tk-str:  oklch(0.55 0.13 170);
    --tk-num:  oklch(0.58 0.16 50);
    --tk-bool: oklch(0.50 0.18 300);
    --tk-date: oklch(0.55 0.16 200);
    --tk-arr:  oklch(0.45 0.06 240);
    --tk-punc: oklch(0.68 0.04 240);
  }

  .ce-code[data-theme="midnight"] {
    --tk-bg:        oklch(0.22 0.015 250);
    --tk-gutter-bg: oklch(0.19 0.015 250);
    --tk-c:    oklch(0.55 0.03 250);
    --tk-sec:  oklch(0.75 0.13 260);
    --tk-key:  oklch(0.92 0.01 250);
    --tk-eq:   oklch(0.55 0.03 250);
    --tk-str:  oklch(0.78 0.12 145);
    --tk-num:  oklch(0.78 0.13 75);
    --tk-bool: oklch(0.72 0.16 30);
    --tk-date: oklch(0.72 0.13 200);
    --tk-arr:  oklch(0.78 0.01 250);
    --tk-punc: oklch(0.48 0.02 250);
  }
  .ce-code[data-theme="midnight"] .ce-gutter {
    color: oklch(0.48 0.02 250);
    border-right-color: oklch(0.28 0.015 250);
  }
  .ce-code[data-theme="midnight"] .ce-ta { caret-color: oklch(0.85 0.15 260); }

  .tk-c   { color: var(--tk-c); font-style: italic; }
  .tk-sec { color: var(--tk-sec); }
  .tk-key { color: var(--tk-key); }
  .tk-eq  { color: var(--tk-eq); }
  .tk-str { color: var(--tk-str); }
  .tk-num { color: var(--tk-num); }
  .tk-bool{ color: var(--tk-bool); }
  .tk-date{ color: var(--tk-date); }
  .tk-arr { color: var(--tk-arr); }
  .tk-punc{ color: var(--tk-punc); }

  .ce-line { display: block; min-height: 20px; }
  .ce-line.mod { background: oklch(0.72 0.13 75 / 0.07); }
  .ce-line.add { background: oklch(0.58 0.13 155 / 0.07); }

  /* ── status bar ──────────────────────────────────────────── */
  .ce-status {
    display: flex; align-items: center;
    border-top: 1px solid var(--rule);
    background: var(--paper-2);
    font-size: 10.5px;
    color: var(--muted);
    padding: 0 14px;
    height: 26px;
    gap: 14px;
    letter-spacing: 0.02em;
    min-width: 0;
    overflow: hidden;
  }
  .ce-status .s { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; flex-shrink: 0; }
  .ce-status .s b { color: var(--ink); font-weight: 500; }
  .ce-status .spacer { flex: 1; }
  .ce-status .s.warn b { color: var(--warn); }
  .ce-status .s.pos b { color: var(--pos); }

  .ce-theme {
    background: transparent;
    border: 0;
    color: var(--muted);
    font-family: var(--mono);
    font-size: 10.5px;
    letter-spacing: 0.02em;
    cursor: pointer;
    padding: 0;
    outline: none;
  }
  .ce-theme:hover { color: var(--ink); }
  .ce-theme option { background: var(--paper); color: var(--ink); }

  /* ── empty state ─────────────────────────────────────────── */
  .ce-empty {
    display: flex; align-items: center; justify-content: center;
    height: 100%;
    color: var(--muted);
    font-size: 12px;
  }

  /* ── commit modal ────────────────────────────────────────── */
  .ce-modal-bg {
    position: fixed; inset: 0; z-index: 250;
    background: oklch(0 0 0 / 0.4);
    backdrop-filter: blur(2px);
    display: flex; align-items: center; justify-content: center;
    padding: 24px;
  }
  .ce-modal {
    width: 540px;
    max-width: 100%;
    background: var(--paper);
    border: 1px solid var(--rule);
    box-shadow: 0 12px 48px oklch(0 0 0 / 0.25);
    display: flex; flex-direction: column;
    font-family: var(--mono);
  }
  .ce-modal-h {
    padding: 14px 18px;
    border-bottom: 1px solid var(--rule);
    display: flex; align-items: center; justify-content: space-between;
  }
  .ce-modal-h .t { font-size: 14px; color: var(--ink); letter-spacing: -0.01em; }
  .ce-modal-h .sub { font-size: 10.5px; color: var(--muted); margin-top: 2px; letter-spacing: 0.06em; text-transform: uppercase; }
  .ce-modal-x {
    background: transparent; border: 0; color: var(--muted);
    font-size: 18px; cursor: pointer; padding: 2px 6px;
  }
  .ce-modal-x:hover { color: var(--ink); }
  .ce-modal-b {
    padding: 16px 18px;
    display: flex; flex-direction: column; gap: 14px;
    border-bottom: 1px solid var(--rule);
  }
  .ce-field { display: flex; flex-direction: column; gap: 6px; }
  .ce-field .lbl {
    font-size: 9.5px; color: var(--muted);
    letter-spacing: 0.1em; text-transform: uppercase;
  }
  .ce-field input, .ce-field textarea {
    background: var(--paper-2);
    border: 1px solid var(--rule-2);
    color: var(--ink);
    padding: 8px 10px;
    font-family: var(--mono); font-size: 12px;
    outline: none; resize: vertical;
  }
  .ce-field input:focus, .ce-field textarea:focus { border-color: var(--accent); }
  .ce-field textarea { min-height: 60px; }

  .ce-toggle-row {
    display: flex; gap: 8px;
  }
  .ce-toggle {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid var(--rule-2);
    background: var(--paper);
    color: var(--ink-2);
    font-family: var(--mono); font-size: 11px;
    cursor: pointer;
    text-align: left;
    display: flex; flex-direction: column; gap: 2px;
  }
  .ce-toggle[data-on="true"] {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--ink);
  }
  .ce-toggle .lbl { font-size: 11px; color: var(--ink); }
  .ce-toggle .sub { font-size: 10px; color: var(--muted); }
  .ce-toggle[data-on="true"] .lbl { color: var(--accent); }

  .ce-impact {
    background: var(--paper-2);
    border: 1px solid var(--rule);
    padding: 10px 12px;
    font-size: 11px;
  }
  .ce-impact .h {
    font-size: 9.5px; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--muted); margin-bottom: 6px;
  }
  .ce-impact .row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 10px;
    padding: 5px 0;
    border-top: 1px dashed var(--rule);
    align-items: center;
  }
  .ce-impact .row:first-of-type { border-top: 0; }
  .ce-impact .led {
    width: 6px; height: 6px; background: var(--pos);
  }
  .ce-impact .nm { color: var(--ink); }
  .ce-impact .sub { color: var(--muted); font-size: 10px; }

  .ce-modal-f {
    padding: 12px 18px;
    display: flex; gap: 8px; justify-content: flex-end;
    background: var(--paper-2);
  }

  .ce-progress {
    display: flex; flex-direction: column; gap: 10px;
    padding: 8px 0;
    font-size: 11.5px;
  }
  .ce-progress .step {
    display: grid;
    grid-template-columns: 16px 1fr auto;
    gap: 10px; align-items: center;
    color: var(--muted);
  }
  .ce-progress .step .ic {
    width: 10px; height: 10px;
    border: 1px solid var(--rule-2);
    background: var(--paper);
  }
  .ce-progress .step[data-state="done"]    { color: var(--ink); }
  .ce-progress .step[data-state="done"] .ic { background: var(--pos); border-color: var(--pos); }
  .ce-progress .step[data-state="active"]  { color: var(--ink); }
  .ce-progress .step[data-state="active"] .ic {
    background: var(--accent); border-color: var(--accent);
    animation: ce-pulse 1.4s ease-out infinite;
  }
  @keyframes ce-pulse {
    0%   { box-shadow: 0 0 0 0 var(--accent); }
    70%  { box-shadow: 0 0 0 5px transparent; }
    100% { box-shadow: 0 0 0 0 transparent; }
  }
  .ce-progress .step .sub { font-size: 10px; color: var(--muted); }

  @media (max-width: 900px) {
    .ce { grid-template-columns: 1fr; height: auto; }
    .ce-side { border-right: 0; border-bottom: 1px solid var(--rule); }
  }
  `;
  const s = document.createElement("style");
  s.id = "cfg-editor-css";
  s.textContent = css;
  document.head.appendChild(s);
})();

/* ─── TOML tokenizer (line-by-line) ───────────────────────────── */
function tokenizeToml(line){
  // returns array of { cls, txt }
  const out = [];
  let s = line;

  // full-line comment
  const m1 = s.match(/^(\s*)(#.*)$/);
  if (m1){
    if (m1[1]) out.push({cls:"", txt:m1[1]});
    out.push({cls:"tk-c", txt:m1[2]});
    return out;
  }
  // section
  const m2 = s.match(/^(\s*)(\[\[?[\w.\-]+\]?\])(\s*.*)$/);
  if (m2){
    if (m2[1]) out.push({cls:"", txt:m2[1]});
    out.push({cls:"tk-sec", txt:m2[2]});
    if (m2[3]) out.push({cls:"", txt:m2[3]});
    return out;
  }
  // key = value (and trailing ; key = value chains)
  // split by ; carefully but leave inside strings alone
  const segments = splitTopLevel(s, ";");
  segments.forEach((seg, i) => {
    if (i > 0) out.push({cls:"tk-punc", txt:";"});
    const m3 = seg.match(/^(\s*)([\w.\-]+)(\s*)(=)(\s*)(.*)$/);
    if (m3){
      if (m3[1]) out.push({cls:"", txt:m3[1]});
      out.push({cls:"tk-key", txt:m3[2]});
      if (m3[3]) out.push({cls:"", txt:m3[3]});
      out.push({cls:"tk-eq", txt:"="});
      if (m3[5]) out.push({cls:"", txt:m3[5]});
      const value = m3[6];
      // trailing inline comment?
      const cm = value.match(/^(.*?)(\s*#.*)$/);
      const valPart = cm ? cm[1] : value;
      pushValue(out, valPart);
      if (cm) out.push({cls:"tk-c", txt:cm[2]});
    } else {
      out.push({cls:"", txt:seg});
    }
  });
  return out;
}

function splitTopLevel(s, ch){
  const out = [];
  let buf = "";
  let inStr = false, q = "";
  for (let i = 0; i < s.length; i++){
    const c = s[i];
    if (inStr){
      buf += c;
      if (c === q && s[i-1] !== "\\") inStr = false;
      continue;
    }
    if (c === "\"" || c === "'") { inStr = true; q = c; buf += c; continue; }
    if (c === ch){ out.push(buf); buf = ""; continue; }
    buf += c;
  }
  out.push(buf);
  return out;
}

function pushValue(out, v){
  if (!v) return;
  // array
  if (v.trim().startsWith("[")){
    // simple: color brackets, recurse on items
    let i = 0;
    while (i < v.length){
      const c = v[i];
      if (c === "[" || c === "]" || c === ","){
        out.push({cls:"tk-arr", txt:c});
        i++; continue;
      }
      if (c === "\"" || c === "'"){
        // find end
        const q = c;
        let j = i+1;
        while (j < v.length && !(v[j] === q && v[j-1] !== "\\")) j++;
        out.push({cls:"tk-str", txt:v.slice(i, j+1)});
        i = j+1; continue;
      }
      if (/\s/.test(c)){ out.push({cls:"", txt:c}); i++; continue; }
      // bare token until next comma/bracket/space
      let j = i;
      while (j < v.length && !/[,\[\]\s]/.test(v[j])) j++;
      const tok = v.slice(i, j);
      out.push({cls: classifyScalar(tok), txt: tok});
      i = j;
    }
    return;
  }
  out.push({cls: classifyScalar(v.trim() === v ? v : v.trim()), txt: v});
}

function classifyScalar(t){
  if (t === "true" || t === "false") return "tk-bool";
  if (/^"([^"\\]|\\.)*"$/.test(t) || /^'([^'\\]|\\.)*'$/.test(t)) return "tk-str";
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return "tk-date";
  if (/^-?\d+(\.\d+)?$/.test(t)) return "tk-num";
  return "tk-key";
}

/* ─── diff: which lines differ between original and current ── */
function diffLines(a, b){
  const aL = a.split("\n"), bL = b.split("\n");
  const mod = new Set(), add = new Set();
  const n = Math.max(aL.length, bL.length);
  for (let i = 0; i < n; i++){
    if (i >= aL.length) add.add(i);
    else if (i >= bL.length) {/* line was deleted; ignore for now */}
    else if (aL[i] !== bL[i]) mod.add(i);
  }
  return { mod, add, count: mod.size + add.size };
}

/* ─── CodeArea: overlay textarea + highlighted pre ───────────── */
function CodeArea({ value, onChange, readOnly, diff, onCursor, theme, overrideLines, scrollRef }){
  const taRef = useCfgR(null);
  const lines = value.split("\n");

  const handleScroll = () => {};

  const handleSelect = (e) => {
    const ta = e.target;
    const pos = ta.selectionStart;
    const before = ta.value.slice(0, pos);
    const ln = before.split("\n").length;
    const col = before.length - before.lastIndexOf("\n");
    onCursor && onCursor({ line: ln, col });
  };

  const handleKey = (e) => {
    if (readOnly) return;
    if (e.key === "Tab"){
      e.preventDefault();
      const ta = e.target;
      const s = ta.selectionStart, en = ta.selectionEnd;
      const v = ta.value;
      const next = v.slice(0,s) + "  " + v.slice(en);
      onChange(next);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
    }
  };

  return (
    <div className="ce-code" data-theme={theme || "aviaf"}>
      <div className="ce-scroll" ref={scrollRef} onScroll={handleScroll}>
        <div className="ce-grid">
          <div className="ce-gutter">
            {lines.map((_, i) => {
              const isMod = diff && diff.mod.has(i);
              const isAdd = diff && diff.add.has(i);
              const isOv = overrideLines && overrideLines.has(i);
              const cls = isMod ? "ln mod" : isAdd ? "ln add" : "ln";
              return (
                <div key={i} className={cls}>
                  {isOv && <span className="ov-dot" title="hub override active on this param" />}
                  {i+1}
                </div>
              );
            })}
          </div>
          <div className="ce-pane">
            <pre className="ce-pre" aria-hidden="true">
              {lines.map((line, i) => {
                const cls = "ce-line " + (diff && diff.mod.has(i) ? "mod" : diff && diff.add.has(i) ? "add" : "");
                const tokens = tokenizeToml(line);
                return (
                  <span key={i} className={cls}>
                    {tokens.length === 0
                      ? <span> </span>
                      : tokens.map((t, j) => (
                          <span key={j} className={t.cls}>{t.txt}</span>
                        ))}
                    {"\n"}
                  </span>
                );
              })}
            </pre>
            {!readOnly && (
              <textarea
                ref={taRef}
                className="ce-ta"
                value={value}
                spellCheck={false}
                onChange={(e) => onChange(e.target.value)}
                onSelect={handleSelect}
                onClick={handleSelect}
                onKeyUp={handleSelect}
                onKeyDown={handleKey}
                style={{ height: (lines.length * 20 + 24) + "px" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── commit modal ───────────────────────────────────────────── */
function CommitModal({ cfg, instances, diff, onClose, onConfirmed }){
  const [msg, setMsg] = useCfg(`tune ${cfg.label}`);
  const [target, setTarget] = useCfg("main");        // main | branch
  const [reload, setReload] = useCfg(true);          // hot-reload instances?
  const [phase, setPhase] = useCfg("compose");       // compose | running | done
  const [step, setStep] = useCfg(0);

  const live = instances.filter(i => i.state === "running");

  useCfgE(() => {
    if (phase !== "running") return;
    const steps = ["lint", "commit", "push", "deploy"];
    let i = 0;
    setStep(0);
    const tick = () => {
      i += 1;
      if (i >= steps.length) {
        setStep(steps.length);
        setTimeout(() => setPhase("done"), 350);
        return;
      }
      setStep(i);
      setTimeout(tick, 480 + Math.random() * 380);
    };
    const t = setTimeout(tick, 600);
    return () => clearTimeout(t);
  }, [phase]);

  const startUpload = () => setPhase("running");

  return (
    <div className="ce-modal-bg" onClick={(e)=>{ if (e.target === e.currentTarget && phase !== "running") onClose(); }}>
      <div className="ce-modal">
        <div className="ce-modal-h">
          <div>
            <div className="t">{phase === "done" ? "Deployed" : phase === "running" ? "Uploading…" : "Upload to aviaf"}</div>
            <div className="sub">{cfg.file}</div>
          </div>
          {phase !== "running" && <button className="ce-modal-x" onClick={onClose}>×</button>}
        </div>

        {phase === "compose" && (
          <>
            <div className="ce-modal-b">
              <div className="ce-field">
                <span className="lbl">Commit message</span>
                <textarea value={msg} onChange={(e)=>setMsg(e.target.value)} />
              </div>

              <div className="ce-field">
                <span className="lbl">Target</span>
                <div className="ce-toggle-row">
                  <button className="ce-toggle" data-on={target === "main"} onClick={()=>setTarget("main")}>
                    <span className="lbl">main</span>
                    <span className="sub">direct commit · ahead 1</span>
                  </button>
                  <button className="ce-toggle" data-on={target === "branch"} onClick={()=>setTarget("branch")}>
                    <span className="lbl">feature/tune-{cfg.id.replace("cfg-","")}</span>
                    <span className="sub">open PR · review required</span>
                  </button>
                </div>
              </div>

              <div className="ce-field">
                <span className="lbl">Hot-reload</span>
                <div className="ce-toggle-row">
                  <button className="ce-toggle" data-on={reload} onClick={()=>setReload(true)}>
                    <span className="lbl">reload running instances</span>
                    <span className="sub">flat & re-arm · ~3s</span>
                  </button>
                  <button className="ce-toggle" data-on={!reload} onClick={()=>setReload(false)}>
                    <span className="lbl">file only</span>
                    <span className="sub">picked up at next start</span>
                  </button>
                </div>
              </div>

              <div className="ce-impact">
                <div className="h">Impact · {instances.length} instance{instances.length === 1 ? "" : "s"}</div>
                {instances.length === 0 && <div style={{color:"var(--muted)", fontSize:11}}>no instances bound to this config</div>}
                {instances.map(i => {
                  const acct = window.ACCOUNTS.find(a => a.id === i.account);
                  return (
                    <div className="row" key={i.id}>
                      <span className="led" style={{background: i.state === "running" ? "var(--pos)" : "var(--muted-2)"}} />
                      <span>
                        <span className="nm">{i.id}</span>
                        <span className="sub" style={{marginLeft:6}}>{i.host} · {acct ? acct.label : "—"}</span>
                      </span>
                      <span className="sub">{reload && i.state === "running" ? "will reload" : i.state}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{fontSize:11, color:"var(--muted)"}}>
                {diff.count} line{diff.count === 1 ? "" : "s"} changed · {diff.mod.size} modified · {diff.add.size} added
              </div>
            </div>
            <div className="ce-modal-f">
              <button className="ce-btn" onClick={onClose}>Cancel</button>
              <button className="ce-btn primary" disabled={!msg.trim()} onClick={startUpload}>
                {target === "main" ? "Commit & deploy" : "Push branch & open PR"}
              </button>
            </div>
          </>
        )}

        {phase === "running" && (
          <>
            <div className="ce-modal-b">
              <div className="ce-progress">
                {["lint config schema", "git commit -m \""+msg.replace(/"/g,"\\\"").slice(0,40)+"…\"", "git push origin "+ (target==="main"?"main":"feature/tune-"+cfg.id.replace("cfg-","")), reload ? "deploy → reload "+instances.length+" instance"+(instances.length===1?"":"s") : "deploy → file only"].map((label, i) => (
                  <div key={i} className="step" data-state={i < step ? "done" : i === step ? "active" : ""}>
                    <span className="ic" />
                    <span>{label}</span>
                    <span className="sub">{i < step ? "ok" : i === step ? "…" : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {phase === "done" && (
          <>
            <div className="ce-modal-b">
              <div style={{fontSize:12, color:"var(--ink)"}}>
                Committed as <span className="tk-sec" style={{color:"var(--accent)"}}>{randSha()}</span> on {target === "main" ? "main" : "feature/tune-"+cfg.id.replace("cfg-","")}
              </div>
              {reload && live.length > 0 && (
                <div style={{fontSize:11, color:"var(--muted)"}}>
                  Hot-reloaded {live.length} running instance{live.length===1?"":"s"} · flat & re-armed in ~{(2+Math.random()).toFixed(1)}s
                </div>
              )}
              <div className="ce-impact">
                <div className="h">Status</div>
                {instances.map(i => (
                  <div className="row" key={i.id}>
                    <span className="led" style={{background:"var(--pos)"}} />
                    <span className="nm">{i.id}</span>
                    <span className="sub">running · new config loaded</span>
                  </div>
                ))}
                {instances.length === 0 && <div style={{color:"var(--muted)", fontSize:11}}>config saved · not yet attached</div>}
              </div>
            </div>
            <div className="ce-modal-f">
              <button className="ce-btn primary" onClick={() => onConfirmed && onConfirmed()}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function randSha(){
  const c = "0123456789abcdef";
  let s = "";
  for (let i = 0; i < 7; i++) s += c[Math.floor(Math.random()*16)];
  return s;
}

/* ─── binding pill — instance → account → strategy → overrides ─── */
function BindingPill({ cfg, instances, overrides, onJumpToLine }){
  const [open, setOpen] = useCfg(false);
  const popRef = useCfgR(null);

  useCfgE(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!cfg) return null;

  // collect attached accounts + strategies from instance data
  const bindings = (instances || []).map(i => {
    const acct = (window.ACCOUNTS || []).find(a => a.id === i.account);
    const defs = (cfg.strategyDefs || []).map(id => (window.STRATEGY_DEFS || []).find(s => s.id === id)).filter(Boolean);
    return { inst: i, acct, defs };
  });

  // compact summary
  let summary;
  if (bindings.length === 0) {
    summary = <span className="chip" style={{color:"var(--muted)"}}>unattached</span>;
  } else if (bindings.length === 1) {
    const b = bindings[0];
    summary = (
      <>
        <span className="chip"><b>{b.inst.id}</b></span>
        <span className="sep">/</span>
        <span className="chip"><b>{b.acct ? b.acct.code : "—"}</b></span>
        <span className="sep">/</span>
        <span className="chip"><b>{cfg.strategyDefs.length === 1 ? cfg.strategyDefs[0].replace("sd-","") : cfg.strategyDefs.length + " strats"}</b></span>
      </>
    );
  } else {
    const acctCt = new Set(bindings.map(b => b.acct && b.acct.id).filter(Boolean)).size;
    summary = (
      <>
        <span className="chip"><b>{bindings.length}</b> instances</span>
        <span className="sep">·</span>
        <span className="chip"><b>{acctCt}</b> accounts</span>
        <span className="sep">·</span>
        <span className="chip"><b>{cfg.strategyDefs.length}</b> strats</span>
      </>
    );
  }

  const liveCt = bindings.filter(b => b.inst.state === "running").length;
  const led = bindings.length === 0 ? "idle" : (liveCt > 0 ? "" : "warn");

  return (
    <div className="ce-bind" data-open={open}
         onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
      <span className={"led " + led} />
      {summary}
      {overrides.length > 0 && (
        <>
          <span className="sep">·</span>
          <span className="ov">{overrides.length} override{overrides.length===1?"":"s"}</span>
        </>
      )}
      <span className="chev">{open ? "▴" : "▾"}</span>

      {open && (
        <div className="ce-bind-pop" ref={popRef} onClick={(e) => e.stopPropagation()}>
          {bindings.length === 0 && (
            <div className="group">
              <div className="g-h"><span>attached</span></div>
              <div className="empty">No instances currently bind this config. It will be loaded at next agent start — or attach manually from the Instances page.</div>
            </div>
          )}

          {bindings.map(({inst, acct, defs}) => (
            <React.Fragment key={inst.id}>
              <div className="group">
                <div className="g-h">
                  <span>instance</span>
                  <span className="ct">
                    <a className="link" href={"#/instances/"+inst.id}
                       onClick={() => setOpen(false)}>open →</a>
                  </span>
                </div>
                <div className="row">
                  <span className="nm">{inst.id}</span>
                  <span className={"state " + (inst.state === "running" ? "live" : "idle")}>
                    {inst.state === "running" ? "● running" : inst.state}
                  </span>
                  <span className="meta">
                    <b>host</b>{inst.host} <b>pid</b>{inst.pid} <b>v</b>{inst.version || "0.4.2"} <b>up</b>{inst.uptime}
                  </span>
                </div>
              </div>

              {acct && (
                <div className="group">
                  <div className="g-h"><span>account</span><span className="ct">{acct.broker}</span></div>
                  <div className="row">
                    <span className="nm">{acct.code}<span className="src">{acct.source || "agent"}</span></span>
                    <span className="state">{acct.kind}</span>
                    <span className="meta">
                      <b>equity</b>${acct.equity ? acct.equity.toLocaleString() : "—"}
                      <b>session-start</b>${acct.sessionStart ? acct.sessionStart.toLocaleString() : "—"}
                      <b>dlc</b>${cfg.dailyLossCap.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              <div className="group">
                <div className="g-h"><span>strateg{defs.length === 1 ? "y" : "ies"}</span><span className="ct">{defs.length}</span></div>
                {defs.map(d => (
                  <div className="row" key={d.id}>
                    <span className="nm">{d.id}</span>
                    <span className="state">{d.klass}</span>
                    <span className="meta">
                      <b>symbol</b>{d.symbol}.{(d.venue||"").slice(0,3)}
                      <b>basis</b>{d.basis}
                    </span>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}

          <div className="group">
            <div className="g-h">
              <span>live overrides</span>
              <span className="ct">{overrides.length} ahead of TOML</span>
            </div>
            {overrides.length === 0 && (
              <div className="empty">No active hub-side overrides. Agent runs the file as committed.</div>
            )}
            {overrides.map((o, i) => (
              <div className="ovr" key={i} onClick={() => { onJumpToLine && onJumpToLine(o.line); setOpen(false); }}>
                <div className="p">
                  <span className="dot" />
                  <span className="pp">
                    {o.account && <span className="k">{o.account}.</span>}
                    <span className="k">{o.path}</span>
                  </span>
                </div>
                <div className="v">
                  <span className="from">{o.toml}</span>
                  <span className="to">{o.live}</span>
                  <span className="by">· {o.by} · {o.when}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── editor hub ─────────────────────────────────────────────── */
function ConfigEditorHub({ initialId }){
  const liveConfigs = window.CONFIGS || [];
  const demoConfigs = window.CONFIGS_DEMO || [];
  // Live wins on id collision: a real config with id "cfg-mgc-alpha"
  // hides the demo of the same name (so production reality shows
  // through if/when someone names a real config after a demo).
  const liveIds = new Set(liveConfigs.map(c => c.id));
  const allConfigs = [...liveConfigs, ...demoConfigs.filter(c => !liveIds.has(c.id))];
  const [activeId, setActiveId] = useCfg(initialId || (allConfigs[0] && allConfigs[0].id) || null);
  const [editing, setEditing] = useCfg(false);
  const [drafts, setDrafts] = useCfg({});       // unsaved per-file
  const [originals, setOriginals] = useCfg(() => ({...window.CONFIG_FILES}));
  const [cursor, setCursor] = useCfg({line: 1, col: 1});
  const [modal, setModal] = useCfg(false);
  const [theme, setTheme] = useCfg(() => {
    const allowed = ["aviaf", "mono", "sunset", "ocean", "midnight"];
    const saved = localStorage.getItem("aviaf:cfg:theme");
    return allowed.includes(saved) ? saved : "aviaf";
  });
  const [sideW, setSideW] = useCfg(() => {
    const v = parseInt(localStorage.getItem("aviaf:cfg:sideW"), 10);
    return Number.isFinite(v) ? Math.min(480, Math.max(200, v)) : 260;
  });
  const [histOpen, setHistOpen] = useCfg(() => localStorage.getItem("aviaf:cfg:hist") !== "0");
  const [bottomH, setBottomH] = useCfg(() => {
    const v = parseInt(localStorage.getItem("aviaf:cfg:bottomH"), 10);
    return Number.isFinite(v) ? Math.min(640, Math.max(60, v)) : 300;
  });
  const [bottomCollapsed, setBottomCollapsed] = useCfg(
    () => localStorage.getItem("aviaf:cfg:bottomCollapsed") === "1"
  );
  const dragRef = useCfgR(null);
  const yDragRef = useCfgR(null);

  useCfgE(() => { localStorage.setItem("aviaf:cfg:theme", theme); }, [theme]);
  useCfgE(() => { localStorage.setItem("aviaf:cfg:sideW", String(sideW)); }, [sideW]);
  useCfgE(() => { localStorage.setItem("aviaf:cfg:hist", histOpen ? "1" : "0"); }, [histOpen]);
  useCfgE(() => { localStorage.setItem("aviaf:cfg:bottomH", String(bottomH)); }, [bottomH]);
  useCfgE(() => { localStorage.setItem("aviaf:cfg:bottomCollapsed", bottomCollapsed ? "1" : "0"); }, [bottomCollapsed]);

  const startDrag = (e) => {
    e.preventDefault();
    document.body.classList.add("ce-dragging");
    const onMove = (ev) => {
      const root = e.target.closest(".ce");
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const w = Math.min(480, Math.max(200, ev.clientX - rect.left));
      root.style.setProperty("--ce-side-w", w + "px");
      dragRef.current = w;
    };
    const onUp = () => {
      document.body.classList.remove("ce-dragging");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (dragRef.current != null) setSideW(dragRef.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Vertical drag for the branch+history panel.  Persisted via bottomH;
  // also live-updates --ce-bottom-h on the sidebar element during drag
  // for 60fps response without React re-renders on every mousemove.
  const startYDrag = (e) => {
    e.preventDefault();
    const sideEl = e.target.closest(".ce-side");
    if (!sideEl) return;
    document.body.classList.add("ce-y-dragging");
    e.currentTarget.setAttribute("data-dragging", "true");
    const handle = e.currentTarget;
    const onMove = (ev) => {
      const rect = sideEl.getBoundingClientRect();
      const h = Math.min(640, Math.max(60, rect.bottom - ev.clientY));
      sideEl.style.setProperty("--ce-bottom-h", h + "px");
      yDragRef.current = h;
    };
    const onUp = () => {
      document.body.classList.remove("ce-y-dragging");
      handle.removeAttribute("data-dragging");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (yDragRef.current != null) setBottomH(yDragRef.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  useCfgE(() => {
    if (initialId && initialId !== activeId) setActiveId(initialId);
  }, [initialId]);

  // Auto-select first config once CONFIGS arrives.  Without this, the
  // initial useState captures activeId=null while window.CONFIGS=[] is
  // still empty at first render; later re-renders (from live data
  // updates) read CONFIGS fresh but activeId stays null forever, so
  // cfg=undefined and the empty-state "config not found" fires
  // permanently even though configs ARE loaded.  Watching length
  // (not allConfigs identity) avoids a re-fire on every re-render.
  useCfgE(() => {
    if (!allConfigs.length) return;
    if (activeId && allConfigs.some(c => c.id === activeId)) return;
    setActiveId(allConfigs[0].id);
  }, [allConfigs.length, activeId]);

  // keep URL in sync with selection (best-effort; sandboxed iframes block replaceState)
  useCfgE(() => {
    if (!activeId) return;
    const target = `#/configs/${activeId}`;
    if (location.hash === target) return;
    try { history.replaceState(null, "", target); }
    catch (_) { /* sandboxed origin — skip URL sync */ }
  }, [activeId]);

  const cfg = allConfigs.find(c => c.id === activeId);
  // Priority for the file body:
  //   1. unsaved local draft (operator typed something)
  //   2. CONFIG_FILES — demo TOMLs shipped in configs.jsx for the
  //      built-in mock entries
  //   3. cfg.body — when the agent ships the raw TOML in its
  //      config_inventory event (no-op until the agent change lands)
  //   4. empty string → editor renders the "TOML body not cached"
  //      placeholder
  const original =
    originals[activeId] != null && originals[activeId] !== ""
      ? originals[activeId]
      : (cfg && typeof cfg.body === "string" && cfg.body)
        ? cfg.body
        : "";
  const draft = drafts[activeId] != null ? drafts[activeId] : original;
  const diff = useCfgM(() => diffLines(original, draft), [original, draft]);
  const dirty = diff.count > 0;
  const instances = (window.INSTANCES || []).filter(i => i.config === activeId);
  const liveCount = instances.filter(i => i.state === "running").length;
  const history = (window.CONFIG_GIT || {})[activeId] || [];
  const configOverrides = (window.CONFIG_OVERRIDES || {})[activeId] || [];
  const overrideLines = useCfgM(() => new Set(configOverrides.map(o => o.line - 1)), [configOverrides]);
  const codeScrollRef = useCfgR(null);
  const jumpToLine = (n) => {
    const el = codeScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: Math.max(0, (n - 3) * 20), behavior: "smooth" });
  };

  const setDraft = (txt) => setDrafts(d => ({...d, [activeId]: txt}));

  const onUploaded = () => {
    // accept current draft as new original; commit
    setOriginals(o => ({...o, [activeId]: draft}));
    setDrafts(d => { const nx = {...d}; delete nx[activeId]; return nx; });
    setEditing(false);
    setModal(false);
  };

  const discard = () => {
    setDrafts(d => { const nx = {...d}; delete nx[activeId]; return nx; });
    setEditing(false);
  };

  const lineCount = draft.split("\n").length;

  if (!allConfigs.length || !cfg) {
    return (
      <div style={{
        padding: "60px 24px",
        textAlign: "center",
        color: "var(--muted)",
        fontFamily: "var(--mono)",
        borderTop: "1px solid var(--rule)",
      }}>
        <div style={{fontSize: 13, color: "var(--ink-2)"}}>
          {allConfigs.length ? "config not found" : "no configs yet · waiting for agent"}
        </div>
        <div style={{fontSize: 11, marginTop: 8}}>
          {allConfigs.length
            ? "the requested config id is not loaded"
            : "configs appear once the agent reports its loaded TOML files"}
        </div>
      </div>
    );
  }

  return (
    <div className="ce" style={{"--ce-side-w": sideW + "px"}}>
      {/* sidebar */}
      <aside className="ce-side" style={{"--ce-bottom-h": bottomH + "px"}}>
        <div className="ce-side-h">
          <span>workspace</span>
          <span style={{display:"flex", alignItems:"center"}}>
            <span className="ct">aviaf-configs</span>
            <button className="togg"
                    title={bottomCollapsed ? "show branch + history" : "hide branch + history"}
                    onClick={() => setBottomCollapsed(c => !c)}>
              {bottomCollapsed ? "▴ git" : "▾ hide"}
            </button>
          </span>
        </div>

        <div className="ce-tree-wrap">
          <div className="ce-tree">
            {(() => {
              const liveOnly = allConfigs.filter(c => !c._demo);
              const demoOnly = allConfigs.filter(c => c._demo);
              const renderRow = (c) => {
                const isDirty = (drafts[c.id] != null) && drafts[c.id] !== originals[c.id];
                const usedBy = (window.INSTANCES || []).filter(i => i.config === c.id).length;
                return (
                  <div key={c.id} className="ce-tree-row"
                       data-active={c.id === activeId}
                       data-dirty={isDirty}
                       onClick={() => { setActiveId(c.id); setEditing(false); }}>
                    <span className="ic" />
                    <span style={{minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.file}</span>
                    <span className="meta">{c._demo ? "demo" : (usedBy ? usedBy + "×" : "—")}</span>
                  </div>
                );
              };
              return (
                <>
                  {liveOnly.length > 0 && <div className="ce-folder">configs / live</div>}
                  {liveOnly.map(renderRow)}
                  {demoOnly.length > 0 && <div className="ce-folder">configs / demo</div>}
                  {demoOnly.map(renderRow)}
                </>
              );
            })()}
          </div>
        </div>

        {!bottomCollapsed && (
          <div className="ce-bottom-resizer"
               onMouseDown={startYDrag}
               title="Drag to resize · double-click to hide"
               onDoubleClick={() => setBottomCollapsed(true)} />
        )}
        <div className="ce-bottom" data-collapsed={bottomCollapsed}>
          <div className="ce-branch">
            <div className="ce-branch-row">
              <span className="ce-branch-name"><span className="ic" />main</span>
              <button className="ce-branch-pick">switch…</button>
            </div>
            <div className="ce-branch-status">
              <div className={"s" + (dirty ? " dirty" : "")}>
                <div>working</div>
                <div className="v">{dirty ? diff.count + " ●" : "clean"}</div>
              </div>
              <div className="s">
                <div>ahead</div>
                <div className="v">0</div>
              </div>
              <div className="s">
                <div>behind</div>
                <div className="v">0</div>
              </div>
            </div>
          </div>

          <div className="ce-hist-h" data-open={histOpen} onClick={() => setHistOpen(o => !o)}>
            <span><span className="chev">▾</span> history</span>
            <span className="ct">{history.length} commit{history.length===1?"":"s"}</span>
          </div>
          <div className="ce-hist" data-open={histOpen}>
            {history.length === 0 && (
              <div style={{
                padding: "16px 14px",
                fontSize: 11,
                color: "var(--muted)",
                lineHeight: 1.5,
              }}>
                {cfg && cfg._demo
                  ? "no recorded edits for this demo file"
                  : "git history not yet wired for live configs · would require agent to ship `git log` for each TOML file"}
              </div>
            )}
            {history.map((h, i) => (
              <div className="ce-hist-row" key={i}>
                <span className="sha">{h.sha.slice(0,7)}</span>
                <div>
                  <div className="msg">{h.msg}</div>
                  <div className="sub">{h.author} · {h.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>

      <div className="ce-resizer" onMouseDown={startDrag} title="Drag to resize" />

      {/* main */}
      <div className="ce-main">
        {/* header — single line */}
        <div className="ce-head">
          <div className="ce-crumbs">
            <span>aviaf-configs</span>
            <span className="sep">/</span>
            <span>{cfg && cfg._demo ? "demo" : "live"}</span>
            <span className="sep">/</span>
            <span className="leaf">{cfg ? cfg.file : "—"}</span>
            {dirty && <span className="dirty" title="modified" />}
            <span className="ro" data-state={editing ? "edit" : "ro"}>
              {editing ? "edit" : "read-only"}
            </span>
          </div>
          <span className="ce-head-sep" />
          <div className="ce-head-meta" />
          <BindingPill
            cfg={cfg}
            instances={instances}
            overrides={configOverrides}
            onJumpToLine={jumpToLine}
          />
          <div className="ce-actions">
            {!editing && (
              <>
                <button className="ce-btn" onClick={() => navigator.clipboard && navigator.clipboard.writeText(draft)}>Copy</button>
                <button className="ce-btn" onClick={() => download(cfg.file, draft)}>Download</button>
                <button className="ce-btn primary" onClick={() => setEditing(true)}>Edit</button>
              </>
            )}
            {editing && (
              <>
                <button className="ce-btn danger" onClick={discard}>Discard</button>
                <button className="ce-btn" onClick={() => setEditing(false)} disabled={dirty}>Done</button>
                <button className="ce-btn primary" disabled={!dirty} onClick={() => setModal(true)}>
                  Upload to aviaf →
                </button>
              </>
            )}
          </div>
        </div>

        {/* code (or empty-body placeholder for live configs without
            a cached TOML body — agent's config_inventory event ships
            metadata only) */}
        {original === "" && !editing ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 24px",
            color: "var(--muted)",
            fontFamily: "var(--mono)",
            fontSize: 12,
            lineHeight: 1.6,
            overflow: "auto",
            minHeight: 0,
          }}>
            <div style={{color: "var(--ink-2)", fontSize: 13, marginBottom: 8}}>
              TOML body not shipped by agent
            </div>
            <div style={{maxWidth: 520, textAlign: "center"}}>
              The agent reports this config's metadata (id, file,
              strategies, risk caps) but not the raw TOML body.  To
              light up the editor, have the agent include
              {" "}<code style={{color:"var(--accent)"}}>body=open(path).read()</code>{" "}
              on each item in its <code>config_inventory</code> event —
              the hub forwards <code>body</code> as-is to{" "}
              <code>cfg.body</code>.  Use the binding pill above
              meanwhile.
            </div>
            <div style={{marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center"}}>
              {instances.map(i => (
                <a key={i.id} href={"#/instances/" + i.id}
                   style={{
                     padding: "4px 10px",
                     border: "1px solid var(--rule-2)",
                     color: "var(--ink-2)",
                     textDecoration: "none",
                     fontSize: 11,
                   }}>
                  → inst {i.id}
                </a>
              ))}
              {(cfg.strategyDefs || []).map(sid => (
                <a key={sid} href={"#/strategies/" + sid}
                   style={{
                     padding: "4px 10px",
                     border: "1px solid var(--rule-2)",
                     color: "var(--ink-2)",
                     textDecoration: "none",
                     fontSize: 11,
                   }}>
                  → strat {sid.replace("sd-", "")}
                </a>
              ))}
            </div>
          </div>
        ) : (
          <CodeArea
            value={draft}
            onChange={setDraft}
            readOnly={!editing}
            diff={dirty ? diff : null}
            onCursor={setCursor}
            theme={theme}
            overrideLines={overrideLines}
            scrollRef={codeScrollRef}
          />
        )}

        {/* status bar */}
        <div className="ce-status">
          <span className="s">TOML</span>
          <span className="s">UTF-8</span>
          <span className="s">LF</span>
          <span className="s">2 spaces</span>
          <span className="s"><b>{lineCount}</b> lines</span>
          <span className="s">ln <b>{cursor.line}</b> · col <b>{cursor.col}</b></span>
          <span className="spacer" />
          {dirty && <span className="s warn"><b>● {diff.count}</b> uncommitted</span>}
          {!dirty && <span className="s pos"><b>●</b> clean</span>}
          <span className="s">branch <b>main</b></span>
          <span className="s">last <b>{history[0] ? history[0].sha.slice(0,7) : "—"}</b></span>
          <span className="s">
            theme&nbsp;
            <select className="ce-theme" value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="aviaf">aviaf · default</option>
              <option value="mono">mono</option>
              <option value="sunset">sunset</option>
              <option value="ocean">ocean</option>
              <option value="midnight">midnight</option>
            </select>
          </span>
        </div>
      </div>

      {modal && (
        <CommitModal
          cfg={cfg}
          instances={instances}
          diff={diff}
          onClose={() => setModal(false)}
          onConfirmed={onUploaded}
        />
      )}
    </div>
  );
}

function download(name, text){
  const a = document.createElement("a");
  const b = new Blob([text], { type: "text/plain" });
  a.href = URL.createObjectURL(b);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 100);
}

/* ─── route entry points (override pages.jsx exports) ─────── */
window.PageConfigs = function PageConfigs(){
  return <ConfigEditorHub initialId={null} />;
};
window.PageConfigDetail = function PageConfigDetail({ id }){
  return <ConfigEditorHub initialId={id} />;
};
